import { CompanionFace, type CompanionFaceMood } from '@/components/companion/feed/CompanionFace';
import { CompanionRecentActivity } from '@/components/companion/feed/CompanionRecentActivity';
import type { CompanionFeedState } from '@/components/companion/feed/companion-feed-state';
import { formatCompanionDateTime } from '@/components/companion/feed/companion-formatters';
import { Stack, Text } from '@chakra-ui/react';
import type { CompanionFeedDto } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

/**
 * What the panel says when it has no card to show.
 *
 * Four different pieces of news, told apart on purpose: nothing worth your
 * time, everything already handled, the first briefing still being prepared,
 * and a briefing that did not run or has gone stale. Collapsing them into one
 * "nothing here" would hide a failure behind a quiet night.
 */

interface CompanionPanelEmptyStateProps {
  state: Exclude<CompanionFeedState, 'opportunities'>;
  feed: CompanionFeedDto;
}

const MOOD_BY_STATE: Record<CompanionPanelEmptyStateProps['state'], CompanionFaceMood> = {
  zero: 'doze',
  allHandled: 'happy',
  firstRun: 'look',
  needsCheck: 'doze',
};

export const CompanionPanelEmptyState = ({ state, feed }: CompanionPanelEmptyStateProps) => {
  const { t, i18n } = useTranslation();
  const language = i18n?.language ?? 'en';
  const counters = feed.lastSuccessfulRun?.counters;
  const lastSuccessAt = formatCompanionDateTime(feed.lastSuccessfulRun?.finishedAt, language);

  return (
    <Stack
      gap="10px"
      align="flex-start"
      px="18px"
      py="22px"
      data-testid="companion-panel-empty"
      data-state={state}
    >
      <CompanionFace mood={MOOD_BY_STATE[state]} size={40} />

      <Text fontFamily="heading" fontSize="16px" fontWeight="700" color="ink" lineHeight="1.3">
        {t(`companion.panel.empty.${state}.title`)}
      </Text>

      {state === 'zero' && counters ? (
        <Text fontSize="13.5px" color="inkMuted" lineHeight="1.5">
          {t('companion.panel.empty.zero.body', {
            candidates: counters.candidates,
            reviewed: counters.reviewed,
          })}
        </Text>
      ) : (
        <Text fontSize="13.5px" color="inkMuted" lineHeight="1.5">
          {t(`companion.panel.empty.${state}.body`)}
        </Text>
      )}

      {state === 'needsCheck' && lastSuccessAt ? (
        <Text fontSize="12px" color="inkSubtle">
          {t('companion.panel.empty.needsCheck.lastSuccess', { date: lastSuccessAt })}
        </Text>
      ) : null}

      {state === 'zero' || state === 'allHandled' ? (
        <Text fontSize="12px" color="inkSubtle">
          {t('companion.panel.empty.weekStats', {
            opportunities: feed.weekStats.total,
            executed: feed.weekStats.byOutcome.task_executed,
          })}
        </Text>
      ) : null}

      <CompanionRecentActivity entries={feed.recentActivity} />
    </Stack>
  );
};
